import React, { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Eye, EyeOff, Mail, Lock, User, Chrome, ArrowLeft, AlertTriangle } from 'lucide-react';
import { toast } from 'react-toastify';
import { useAuth } from '../contexts/AuthContext';
import { userApi, referralApi } from '../services/api';
import { useAnalytics } from '../hooks/useAnalytics';
import logger from '../lib/logger';

const Signup: React.FC = () => {
  const navigate = useNavigate();
  const { signup, loginWithGoogle } = useAuth();
  const [searchParams] = useSearchParams();
  const { trackSignupStarted, trackSignupCompleted, trackSignupAbandoned: _trackSignupAbandoned, trackAuthError } = useAnalytics();
  
  const [formData, setFormData] = useState({
    displayName: '',
    email: '',
    password: '',
    confirmPassword: '',
    referralCode: searchParams.get('ref') || ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [validationError, setValidationError] = useState<string | null>(null);
  const [validationChecked, setValidationChecked] = useState(false);
  const [referralValidated, setReferralValidated] = useState(false);
  const [referralValid, setReferralValid] = useState(false);

  // Determine back navigation based on referrer
  const getBackNavigation = () => {
    const from = searchParams.get('from');
    
    if (from === 'landing') {
      return { to: '/', text: 'Back to landing' };
    }
    
    return { to: '/', text: 'Back to home' };
  };
  
  const backNav = getBackNavigation();

  // Validate signup eligibility when email is entered
  const validateSignupEligibility = async (email: string) => {
    if (!email || !email.includes('@')) return;
    
    try {
      const response = await userApi.validateSignup(email);
      const data = response.data;
      
      if (!data.allowed) {
        setValidationError(data.reason);
      } else {
        setValidationError(null);
      }
      setValidationChecked(true);
    } catch (error) {
      logger.error('Error validating signup:', error);
      // Don't block signup on validation error, just warn
      setValidationError(null);
      setValidationChecked(true);
    }
  };

  // Validate referral code
  const validateReferralCode = async (code: string) => {
    if (!code.trim()) {
      setReferralValidated(true);
      setReferralValid(true); // Empty code is valid (optional)
      return;
    }
    
    try {
      const response = await referralApi.validateCode(code.trim());
      setReferralValid(response.valid);
      setReferralValidated(true);
    } catch (error) {
      logger.error('Error validating referral code:', error);
      setReferralValid(false);
      setReferralValidated(true);
    }
  };

  const handleEmailBlur = () => {
    if (formData.email) {
      validateSignupEligibility(formData.email);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.displayName || !formData.email || !formData.password || !formData.confirmPassword) {
      toast.error('Please fill in all fields');
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    if (formData.password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }

    // Final validation check before signup
    if (!validationChecked) {
      await validateSignupEligibility(formData.email);
    }

    if (validationError) {
      toast.error(validationError);
      return;
    }

    try {
      setLoading(true);
      trackSignupStarted('email');
      
      const user = await signup(formData.email, formData.password, formData.displayName);
      
      // Complete signup with referral code if provided
      if (formData.referralCode.trim()) {
        try {
          const response = await userApi.completeSignup(formData.referralCode.trim());
          if (response.data.referral_applied) {
            toast.success(`Account created successfully! ${response.data.referral_message} Welcome to ClipThis!`);
          } else {
            toast.success('Account created successfully! Welcome to ClipThis!');
            if (response.data.referral_message) {
              toast.warn(response.data.referral_message);
            }
          }
        } catch (referralError) {
          logger.error('Error processing referral code:', referralError);
          toast.success('Account created successfully! Welcome to ClipThis!');
          toast.warn('There was an issue processing the referral code, but your account was created successfully.');
        }
      } else {
        toast.success('Account created successfully! Welcome to ClipThis!');
      }
      
      trackSignupCompleted('email', user.uid);
      
      navigate('/');
    } catch (error: any) {
      logger.error('Signup error:', error);
      trackAuthError('email_signup', error.message);
      
      // Handle specific Firebase auth errors
      if (error.code === 'auth/email-already-in-use') {
        toast.error('An account with this email already exists');
      } else if (error.code === 'auth/invalid-email') {
        toast.error('Invalid email address');
      } else if (error.code === 'auth/weak-password') {
        toast.error('Password is too weak');
      } else {
        toast.error('Failed to create account. Please try again');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignup = async () => {
    try {
      setGoogleLoading(true);
      trackSignupStarted('google');
      
      const user = await loginWithGoogle();
      trackSignupCompleted('google', user.uid);
      
      toast.success('Account created successfully! Welcome to ClipThis!');
      navigate('/');
    } catch (error: any) {
      logger.error('Google signup error:', error);
      trackAuthError('google_signup', error.message);
      
      if (error.code === 'auth/popup-closed-by-user') {
        toast.error('Sign-up was cancelled');
      } else if (error.code === 'auth/popup-blocked') {
        toast.error('Popup was blocked. Please allow popups and try again');
      } else {
        toast.error('Failed to sign up with Google');
      }
    } finally {
      setGoogleLoading(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

    return (    <div className="auth-page">      <div className="auth-container min-h-screen flex">        {/* Left side - Form */}        <div className="flex-1 flex flex-col justify-center px-8 sm:px-12 lg:px-16 xl:px-20 max-w-md mx-auto w-full relative">          {/* Animated dot background */}          <div className="absolute inset-0 dot-pattern-animated opacity-40 pointer-events-none" />
        <div className="animate-fade-up relative z-10">
          {/* Back button */}
          <Link 
            to={backNav.to}
            className="inline-flex items-center text-sm text-primary-600 hover:text-black transition-colors mb-8"
          >
            <ArrowLeft size={16} className="mr-2" />
            {backNav.text}
          </Link>

          {/* Header */}
          <div className="mb-8">
            <h1 className="text-4xl font-display font-bold tracking-tight mb-2">
              Create account
            </h1>
            <p className="text-primary-600">
              Start creating viral clips today
            </p>
          </div>

          {/* Google Sign Up */}
          <button
            onClick={handleGoogleSignup}
            disabled={googleLoading || loading}
            className="w-full flex items-center justify-center space-x-3 py-3 px-4 border border-primary-200 rounded-xl hover:border-primary-300 hover:bg-primary-50 transition-colors mb-6 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {googleLoading ? (
              <span className="loading-dots">
                <span></span>
                <span></span>
                <span></span>
              </span>
            ) : (
              <>
                <Chrome size={20} />
                <span className="font-medium">Continue with Google</span>
              </>
            )}
          </button>

          {/* Divider */}
          <div className="relative mb-6">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-primary-200" />
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-2 bg-white text-primary-500">or</span>
            </div>
          </div>

          {/* Email/Password Form */}
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="label">Full Name</label>
              <div className="relative">
                <User size={20} className="absolute left-3 top-1/2 -translate-y-1/2 text-primary-400" />
                <input
                  type="text"
                  name="displayName"
                  value={formData.displayName}
                  onChange={handleInputChange}
                  className="input-field pl-11"
                  placeholder="Enter your full name"
                  disabled={loading || googleLoading}
                />
              </div>
            </div>

            <div>
              <label className="label">Email</label>
              <div className="relative">
                <Mail size={20} className="absolute left-3 top-1/2 -translate-y-1/2 text-primary-400" />
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={(e) => {
                    handleInputChange(e);
                    setValidationChecked(false);
                    setValidationError(null);
                  }}
                  onBlur={handleEmailBlur}
                  className={`input-field pl-11 ${validationError ? 'border-red-500 focus:ring-red-500' : ''}`}
                  placeholder="Enter your email"
                  disabled={loading || googleLoading}
                />
              </div>
              
              {/* Validation error message */}
              {validationError && (
                <div className="flex items-center space-x-2 text-red-600 text-sm mt-2">
                  <AlertTriangle className="w-4 h-4" />
                  <span>{validationError}</span>
                </div>
              )}
            </div>

            <div>
              <label className="label">Password</label>
              <div className="relative">
                <Lock size={20} className="absolute left-3 top-1/2 -translate-y-1/2 text-primary-400" />
                <input
                  type={showPassword ? 'text' : 'password'}
                  name="password"
                  value={formData.password}
                  onChange={handleInputChange}
                  className="input-field pl-11 pr-11"
                  placeholder="Create a password"
                  disabled={loading || googleLoading}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-primary-400 hover:text-primary-600 transition-colors"
                >
                  {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>
            </div>

            <div>
              <label className="label">Confirm Password</label>
              <div className="relative">
                <Lock size={20} className="absolute left-3 top-1/2 -translate-y-1/2 text-primary-400" />
                <input
                  type={showConfirmPassword ? 'text' : 'password'}
                  name="confirmPassword"
                  value={formData.confirmPassword}
                  onChange={handleInputChange}
                  className="input-field pl-11 pr-11"
                  placeholder="Confirm your password"
                  disabled={loading || googleLoading}
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-primary-400 hover:text-primary-600 transition-colors"
                >
                  {showConfirmPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>
            </div>

            <div>
              <label className="label">Referral Code (Optional)</label>
              <div className="relative">
                <input
                  type="text"
                  name="referralCode"
                  value={formData.referralCode}
                  onChange={handleInputChange}
                  onBlur={() => validateReferralCode(formData.referralCode)}
                  className={`input-field ${
                    formData.referralCode && referralValidated 
                      ? (referralValid ? 'border-gray-500' : 'border-red-500')
                      : ''
                  }`}
                  placeholder="Enter friend's referral code"
                  disabled={loading || googleLoading}
                />
              </div>
              {formData.referralCode && referralValidated && !referralValid && (
                <div className="flex items-center space-x-2 text-red-600 text-sm mt-2">
                  <AlertTriangle className="w-4 h-4" />
                  <span>Invalid referral code</span>
                </div>
              )}
              {formData.referralCode && referralValidated && referralValid && formData.referralCode.trim() && (
                <div className="flex items-center space-x-2 text-gray-700 text-sm mt-2">
                  <span>✓ Valid referral code - you'll both get an extra video per month!</span>
                </div>
              )}
            </div>

            <div className="pt-2">
              <label className="flex items-start">
                <input
                  type="checkbox"
                  required
                  className="w-4 h-4 rounded border-primary-300 text-black focus:ring-black mt-1"
                />
                <span className="ml-2 text-sm text-primary-600">
                  I agree to the{' '}
                  <Link to="/terms" className="text-black hover:underline">
                    Terms of Service
                  </Link>
                  {' '}and{' '}
                  <Link to="/privacy" className="text-black hover:underline">
                    Privacy Policy
                  </Link>
                </span>
              </label>
            </div>

            <button
              type="submit"
              disabled={loading || googleLoading}
              className="w-full bg-gradient-to-r from-red-500 to-red-600 text-white py-3 px-4 rounded-xl font-semibold hover:from-red-600 hover:to-red-700 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? (
                <span className="loading-dots">
                  <span></span>
                  <span></span>
                  <span></span>
                </span>
              ) : (
                'Create Account'
              )}
            </button>
          </form>

          {/* Sign in link */}
          <p className="text-center text-sm text-primary-600 mt-6">
            Already have an account?{' '}
            <Link
              to="/login"
              className="font-medium text-red-600 hover:text-red-700 transition-colors"
            >
              Sign in
            </Link>
          </p>
        </div>
      </div>

              {/* Right side - Feature showcase */}        <div className="hidden lg:flex flex-1 bg-black text-white flex-col justify-center px-12 xl:px-16 relative">          {/* Animated dots on right side */}          <div className="absolute inset-0 dot-pattern-animated-dark pointer-events-none" />          <div className="animate-fade-up relative z-10" style={{ animationDelay: '0.1s' }}>            <h2 className="text-4xl font-display font-bold mb-6">              Join thousands of creators            </h2>            <p className="text-lg text-gray-300 mb-8">              Transform your content and grow your audience with AI-powered clip generation.            </p>                        <div className="space-y-6">              <div className="flex items-start space-x-4 stagger-item">                <div className="w-8 h-8 bg-white text-black rounded-full flex items-center justify-center font-bold text-sm mt-1 animate-pulse-subtle">                  1                </div>                <div>                  <h3 className="font-semibold mb-1">Upload your stream</h3>                  <p className="text-gray-400 text-sm">Drop in your video and let AI do the work</p>                </div>              </div>                            <div className="flex items-start space-x-4 stagger-item">                <div className="w-8 h-8 bg-white text-black rounded-full flex items-center justify-center font-bold text-sm mt-1 animate-pulse-subtle">                  2                </div>                <div>                  <h3 className="font-semibold mb-1">AI finds highlights</h3>                  <p className="text-gray-400 text-sm">Advanced algorithms detect your best moments</p>                </div>              </div>                            <div className="flex items-start space-x-4 stagger-item">                <div className="w-8 h-8 bg-white text-black rounded-full flex items-center justify-center font-bold text-sm mt-1 animate-pulse-subtle">                  3                </div>                <div>                  <h3 className="font-semibold mb-1">Download & share</h3>                  <p className="text-gray-400 text-sm">Get viral-ready clips in minutes</p>                </div>              </div>            </div>          </div>        </div>      </div>    </div>  );
};

export default Signup; 